import * as tslib_1 from "tslib";
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { PushNotificationsService } from '@core/services/push-notifications/push-notifications.service';
import { SocketService } from '@core/services/socket.service';
import { getErrorCode } from '@core/utils/get-error-code';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { PasswordValidation } from '@validators/password-validation';
import { sentry } from 'sentry';
var LoginResetPasswordComponent = /** @class */ (function () {
    function LoginResetPasswordComponent(formBuilder, activatedRoute, navigationService, pushNotificationsService, customMessageService, socketService, fuseProgressBar, authenticationService, router) {
        this.formBuilder = formBuilder;
        this.activatedRoute = activatedRoute;
        this.navigationService = navigationService;
        this.pushNotificationsService = pushNotificationsService;
        this.customMessageService = customMessageService;
        this.socketService = socketService;
        this.fuseProgressBar = fuseProgressBar;
        this.authenticationService = authenticationService;
        this.router = router;
        this.step = 'password';
        this.isLoading = false;
        var email = this.activatedRoute.snapshot.params.email;
        this.verificationCodeForm = this.formBuilder.array([
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required])
        ]);
        this.resetPasswordForm = this.formBuilder.group({
            email: [email, [Validators.required, Validators.email, Validators.pattern(/@.+\./)]],
            password: [null, [
                    Validators.required,
                    Validators.minLength(8),
                    Validators.pattern(new RegExp('[0-9]{1,}')),
                    Validators.pattern(new RegExp('[a-z]{1,}')),
                    Validators.pattern(new RegExp('[A-Z]{1,}')),
                    Validators.pattern(/\^|\$|\*|\.|\[|\]|\{|\}|\(|\)|\?|\-|\"|\!|\@|\#|\%|\&|\/|\\|\,|\>|\<|\'|\:|\;|\||\_|\~|\`|\+|\='/)
                ]],
            confirmPassword: [null, Validators.required]
        }, {
            validator: PasswordValidation.validate.bind(this)
        });
    }
    LoginResetPasswordComponent.prototype.resetPassword = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var input, err_1, code;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.fuseProgressBar.show();
                        this.isLoading = true;
                        input = {
                            email: this.activatedRoute.snapshot.params.email,
                            newPassword: this.resetPasswordForm.value.password,
                            verificationCode: this.verificationCodeForm.value.join('')
                        };
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, 5, 6]);
                        return [4 /*yield*/, this.authenticationService.confirmCodeAndCreateNewPassword(input)];
                    case 2:
                        _a.sent();
                        this.step = 'success';
                        return [4 /*yield*/, this.auth()];
                    case 3:
                        _a.sent();
                        return [3 /*break*/, 6];
                    case 4:
                        err_1 = _a.sent();
                        code = getErrorCode(err_1);
                        if (['ExpiredCodeException', 'CodeMismatchException'].includes(code)) {
                            this.verificationCodeForm.setErrors({ invalid: true });
                            return [2 /*return*/];
                        }
                        if (code === 'InvalidParameterException') {
                            this.resetPasswordForm.get('password').setErrors({ pattern: true });
                            return [2 /*return*/];
                        }
                        if (code === 'LimitExceededException') {
                            this.resetPasswordForm.setErrors({ limitExceeded: true });
                            return [2 /*return*/];
                        }
                        this.resetPasswordForm.setErrors({ unknown: true });
                        return [3 /*break*/, 6];
                    case 5:
                        this.isLoading = false;
                        this.fuseProgressBar.hide();
                        return [7 /*endfinally*/];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    LoginResetPasswordComponent.prototype.auth = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _a, email, password, err_2, code;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 3, 4, 5]);
                        this.isLoading = true;
                        this.fuseProgressBar.show();
                        _a = this.resetPasswordForm.getRawValue(), email = _a.email, password = _a.password;
                        this.resetPasswordForm.get('password').markAsTouched();
                        return [4 /*yield*/, this.authenticationService.login({ provider: 'email-password', email: email, password: password })];
                    case 1:
                        _b.sent();
                        return [4 /*yield*/, this.refreshItemsAndConnectSocket()];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 5];
                    case 3:
                        err_2 = _b.sent();
                        code = getErrorCode(err_2);
                        if (code === 'NewPasswordRequired') {
                            this.toNewPasswordStep();
                            return [2 /*return*/];
                        }
                        if (code === 'SoftwareTokenMfaRequired') {
                            this.toSoftwareTokenStep();
                            return [2 /*return*/];
                        }
                        if (this.resetPasswordForm.get('password').valid) {
                            this.resetPasswordForm.get('password').setErrors({ unknown: true });
                        }
                        sentry.captureException(err_2, {
                            user: {
                                email: this.resetPasswordForm.get('email').value
                            },
                            extra: {
                                message: 'Error on authentication',
                                err: JSON.stringify(err_2)
                            }
                        });
                        return [3 /*break*/, 5];
                    case 4:
                        this.isLoading = false;
                        this.fuseProgressBar.hide();
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    LoginResetPasswordComponent.prototype.authWithSoftwareToken = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var err_3, code;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, 4, 5]);
                        this.isLoading = true;
                        this.fuseProgressBar.show();
                        return [4 /*yield*/, this.authenticationService.loginWithSoftwareToken({
                                email: this.resetPasswordForm.get('email').value,
                                code: this.verificationCodeForm.value.join('')
                            })];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.refreshItemsAndConnectSocket()];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 5];
                    case 3:
                        err_3 = _a.sent();
                        code = getErrorCode(err_3);
                        if (code === 'CodeMismatchException') {
                            this.verificationCodeForm.setErrors({ incorrect: true });
                            return [2 /*return*/];
                        }
                        if (code === 'NotAuthorizedException') {
                            this.resetPasswordForm.reset();
                            this.loginStep = 'email';
                            this.customMessageService.error('LOGIN.AUTH.MFA.EXPIRED_CODE');
                            this.verificationCodeForm.reset();
                            return [2 /*return*/];
                        }
                        if (code === 'UnauthorizedUser') {
                            this.resetPasswordForm.reset();
                            this.loginStep = 'email';
                            this.customMessageService.error('LOGIN.AUTH.ERRORS.UNAUTHORIZED');
                            this.verificationCodeForm.reset();
                            return [2 /*return*/];
                        }
                        this.customMessageService.error('LOGIN.AUTH.ERRORS.UNKNOWN');
                        console.error(err_3);
                        sentry.captureException(err_3, {
                            user: {
                                email: this.resetPasswordForm.get('email').value
                            },
                            extra: {
                                message: 'Error on authentication with verification code',
                                err: JSON.stringify(err_3)
                            }
                        });
                        return [3 /*break*/, 5];
                    case 4:
                        this.isLoading = false;
                        this.fuseProgressBar.hide();
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    LoginResetPasswordComponent.prototype.refreshItemsAndConnectSocket = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var firstVisibleItem, redirectToDestinationUrl;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        firstVisibleItem = this.navigationService.getFirstVisibleItem(true);
                        redirectToDestinationUrl = localStorage.getItem('REDIRECT_TO_URL_AFTER_LOGIN');
                        location.href = redirectToDestinationUrl || firstVisibleItem.url;
                        if (redirectToDestinationUrl) {
                            localStorage.removeItem('REDIRECT_TO_URL_AFTER_LOGIN');
                        }
                        return [4 /*yield*/, this.pushNotificationsService.refreshNotificationSubscription()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.socketService.reconnect()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    LoginResetPasswordComponent.prototype.toNewPasswordStep = function () {
        this.resetPasswordForm = this.formBuilder.group({
            password: [null, [
                    Validators.required,
                    Validators.minLength(8),
                    Validators.pattern(new RegExp('[0-9]{1,}')),
                    Validators.pattern(new RegExp('[a-z]{1,}')),
                    Validators.pattern(new RegExp('[A-Z]{1,}')),
                    Validators.pattern(/\^|\$|\*|\.|\[|\]|\{|\}|\(|\)|\?|\-|\"|\!|\@|\#|\%|\&|\/|\\|\,|\>|\<|\'|\:|\;|\||\_|\~|\`|\+|\='/)
                ]],
            confirmPassword: [null, Validators.required]
        }, {
            validator: PasswordValidation.validate.bind(this)
        });
        this.loginStep = 'resetPassword';
    };
    LoginResetPasswordComponent.prototype.toSoftwareTokenStep = function () {
        this.verificationCodeForm = this.formBuilder.array([
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required]),
            new FormControl(null, [Validators.required])
        ]);
        this.loginStep = 'softwareToken';
    };
    return LoginResetPasswordComponent;
}());
export { LoginResetPasswordComponent };
